import React, { useMemo } from 'react'
import { Card, InputNumber, Button, Space, Typography, Tooltip } from 'antd'
import { Building, Hash, Clock, Settings, Eye, EyeOff } from 'lucide-react'
import type {
  Material,
  LineItemMaterial,
  Status,
  LineItem
} from '../../types/ffe'
import { formatCurrency } from '../../utils/currency'
import { LineItemStatus } from '../LineItem/LineItemStatus'
import { useFFEStore } from '../../store/ffeStore'

const { Text } = Typography

interface Props {
  material: Material
  lineItem: LineItem
  lineItemMaterial?: LineItemMaterial
  onAssign: (quantity: number) => void
  onRemove: () => void
  onUpdateQuantity: (quantity: number) => void
  onChangeStatus: (status: { status: Status }) => void
  onChangeHideOnDocument: (value: boolean) => void
  onEdit: () => void
  accessories?: boolean
}

export const MaterialCard: React.FC<Props> = ({
  material,
  lineItemMaterial,
  onAssign,
  onRemove,
  onUpdateQuantity,
  onChangeStatus,
  onChangeHideOnDocument,
  accessories,
  onEdit,
}) => {
  const suppliers = useFFEStore((state) => state.suppliers)
  const isAssigned = !!lineItemMaterial
  const total = isAssigned
    ? material.sellPricePerUnit * lineItemMaterial.quantity
    : 0

  const supplier = useMemo(() => {
    const supplier = suppliers.find((s) => s._id === material.supplier)
    return supplier ? supplier.name : ''
  }, [suppliers, material.supplier])

  const entity = accessories ? 'Accessory' : 'Finish'

  return (
    <Card size="small">
      <div className="flex gap-4">
        {material.image && (
          <img
            src={material.image.url}
            alt={material.image.alt}
            className="w-[70px] h-[70px] object-cover rounded-lg"
          />
        )}

        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <Text strong>{material.name}</Text>
              <Text className="block text-gray-500">{material.type}</Text>
              <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                {material.supplier && (
                  <div className="flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    <span>{supplier}</span>
                  </div>
                )}
                {material.supplierItemCode && (
                  <div className="flex items-center gap-1">
                    <Hash className="h-3 w-3" />
                    <span>{material.supplierItemCode}</span>
                  </div>
                )}
                {material.leadTime && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{material.leadTime}</span>
                  </div>
                )}
              </div>
            </div>

            <Space direction="vertical" align="end">
              <Space>
                {isAssigned && (
                  <LineItemStatus
                    item={lineItemMaterial}
                    onUpdate={onChangeStatus}
                  />
                )}
              </Space>
            </Space>
          </div>

          <Space className="block mt-2 mb-2">
            <Text className="gap-2 flex flex-row">
              <Text>
                Sell Price: {formatCurrency(material.sellPricePerUnit)}
              </Text>
              <Text type="secondary">
                {material.unit ? `per ${material.unit}` : 'each'}
              </Text>
            </Text>
          </Space>
          <Space className="mb-2">
            {isAssigned && (
              <Text>
                <Text>Total: {formatCurrency(total)} per item</Text>
              </Text>
            )}
            <Text type="secondary">Margin: {(material.margin ?? 0).toFixed(1)}%</Text>
          </Space>
          <div className="flex justify-between items-center mt-4">
            <Space>
              {isAssigned ? (
                <>
                  <InputNumber
                    min={0}
                    value={lineItemMaterial.quantity}
                    onChange={(value) => value && onUpdateQuantity(value)}
                    addonAfter={`${material.unit ? `${material.unit} ` : ''}per item`}
                  />
                  <Button danger onClick={onRemove}>
                    Remove
                  </Button>
                </>
              ) : (
                <Button type="primary" onClick={() => onAssign(1)}>
                  Add to Item
                </Button>
              )}
            </Space>

            <Space>
              <Tooltip
                title={
                  lineItemMaterial?.hideOnDocument
                    ? `${entity} is not shown to clients, but is included in the total. Click to toggle.`
                    : `${entity} is shown to clients and included in the total. Click to toggle.`
                }
              >
                {lineItemMaterial?.hideOnDocument && (
                  <Button
                    type="text"
                    icon={<EyeOff className="h-4 w-4" />}
                    onClick={() => onChangeHideOnDocument(false)}
                  />
                )}
                {!lineItemMaterial?.hideOnDocument && (
                  <Button
                    type="text"
                    icon={<Eye className="h-4 w-4" />}
                    onClick={() => onChangeHideOnDocument(true)}
                  />
                )}
              </Tooltip>
              <Button
                type="text"
                icon={<Settings className="h-4 w-4" />}
                onClick={onEdit}
              />
            </Space>
          </div>
        </div>
      </div>
    </Card>
  )
}
